/**
 * @author       denys.yefremov@startupsoft.co (Denys Yefremov)
 * @copyright    Copyright (c) 2022, Zumper
 */

import { is404Error } from '@zcode/error-filters'
import { definitelyError, isFailedToFetchError } from '@zcode/nr-utils'
import * as Api from '@zcode/nr/es/shared/api'
import type { Dispatch } from 'redux'
import { logger } from '../../logger'
import {
  clearSchoolWidgetData,
  doneFetchingSchoolWidgetData,
  errorFetchingSchoolWidgetData,
  setSchoolWidgetData,
  setSelectedDistrictId,
  startFetchingSchoolWidgetData,
} from '../actions/schoolWidget'

interface Props {
  city?: string
  neighborhood?: string
}

const loadSchoolWidgetDistricts =
  ({ city, neighborhood }: Props) =>
  async (dispatch: Dispatch) => {
    if (!city) {
      return
    }

    dispatch(clearSchoolWidgetData())
    dispatch(startFetchingSchoolWidgetData())

    try {
      let response
      if (neighborhood) {
        response = await Api.widgetDistricts.get({
          neighborhood: `${city}/${neighborhood}`,
        })
      } else {
        response = await Api.widgetDistricts.get({ city })
      }

      if (!response) {
        logger.info(
          'There are no school districts for the ',
          neighborhood ? 'neighborhood ' : 'city ',
          neighborhood ?? city
        )
        return
      }

      const { schoolDistricts } = response ?? {}

      dispatch(setSchoolWidgetData(schoolDistricts))
      dispatch(setSelectedDistrictId(schoolDistricts?.[0]?.districtId))
    } catch (err) {
      if (!is404Error(err) && !isFailedToFetchError(err)) {
        // @ts-expect-error
        dispatch(errorFetchingSchoolWidgetData(err.message ?? err))
        const msg = 'Failed to get schools data loadSchoolWidgetData'
        logger.error(msg, city, definitelyError(msg, err))
      }
    } finally {
      dispatch(doneFetchingSchoolWidgetData())
    }
  }

// eslint-disable-next-line import/no-default-export
export default loadSchoolWidgetDistricts
